/**
 * The shared vocabulary for anything the system believes.
 *
 * A reader must be able to tell, without reading a table header, whether a person said something
 * or a machine read it out of a PDF — and whether anyone has checked it.
 */

import { factStatus } from "../lib/knowledgeStatus";
import type { KnowledgeFact } from "../lib/types";

type FactLike = Pick<
  KnowledgeFact,
  "verification_status_code" | "source_type" | "extractor"
>;

export function KnowledgeStatusBadges({
  fact,
  conflicting,
  showOrigin = true,
}: {
  fact: FactLike;
  conflicting?: boolean;
  showOrigin?: boolean;
}) {
  const status = factStatus(fact, { conflicting });
  return (
    <span className="row" style={{ gap: 5, flexWrap: "nowrap" }}>
      <span className={`badge badge--state-${status.state}`}>
        {status.stateLabel}
      </span>
      {showOrigin ? (
        <span
          className={`badge badge--origin-${status.origin}`}
          title={status.originHint}
        >
          {status.originLabel}
        </span>
      ) : null}
    </span>
  );
}

/** The legend, shown once per screen so the vocabulary never has to be guessed. */
export function KnowledgeLegend() {
  return (
    <div className="legend">
      <span>
        <span className="badge badge--state-verified">Human confirmed</span> a
        person here agreed this is what they said — not yet official context
      </span>
      <span>
        <span className="badge badge--origin-reported">Reported</span> someone
        told us
      </span>
      <span>
        <span className="badge badge--origin-extracted">Extracted</span> read
        from a file — not yet checked
      </span>
      <span>
        <span className="badge badge--state-needs_confirmation">
          Needs confirmation
        </span>{" "}
        waiting for a human
      </span>
      <span>
        <span className="badge badge--state-conflicting">Conflicting</span> two
        sources disagree
      </span>
    </div>
  );
}
